"use client";
import useSwr from "swr";
import { getAmountBreakdown, getWallet } from "../../services";
import { GetAmountBreakdownSchema, WalletSchema } from "../../types";
import { PaymentMethods } from "@/lib/common/types";
import { initiatePayment, verifyPayment } from "@/lib/common/service";
import { getErrorMessage } from "@centisystems/lib/utils";
import { useEffect, useState } from "react";
import { useModalState } from "@/lib";
import { validateCartTotalAmount } from "../../helpers/cartMinTotal";

export enum PaymentEntityType {
  QUOTE = "QUOTE",
  CART = "CART",
  ORDER = "ORDER",
  SUBSCRIPTION = "SUBSCRIPTION",
  SHIPPING = "SHIPPING",
  CAMPAIGN = "CAMPAIGN",
  EXTRA_CHARGE_POST_ORDER = "EXTRA_CHARGE_POST_ORDER",
}

export interface PayWithWalletProps {
  isOpen: boolean;
  paymentEntityType: PaymentEntityType;
  paymentKey?: string;
  entityId: string;
  metadata: Record<string, any>;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess: () => Promise<null>;
  onFailure: (error: any) => void;
  onInitiated?: () => void;
  onPending?: () => void;
}

interface UsePayWithWalletReturn {
  isProcessingPayment: boolean;
  isLoading: boolean;
  isValidating: boolean;
  wallet: WalletSchema | undefined;
  hasWallet: boolean;
  lowBalance: boolean;
  openModal: () => void;
  closeModal: () => void;
  modalIsOpen: boolean;
  handlePayment: () => Promise<void>;
  amountBreakdown: GetAmountBreakdownSchema | undefined;
}

export function usePayWithWallet(
  props: PayWithWalletProps,
): UsePayWithWalletReturn {
  const { onFailure, onSuccess, isOpen, paymentKey } = props;
  const { openModal, closeModal, modalIsOpen } = useModalState();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const {
    isLoading,
    isValidating,
    data: wallet,
    mutate: walletMutate,
  } = useSwr<WalletSchema>("wallets", getWallet);

  const {
    data: amountBreakdown,
    isLoading: amountBreakdownIsLoading,
    isValidating: amountBreakdownIsValidating,
    mutate: amountBreakdownMutate,
  } = useSwr<GetAmountBreakdownSchema>(paymentKey, () =>
    getAmountBreakdown(props.paymentEntityType, props.entityId),
  );

  useEffect(() => {
    if (isOpen) {
      walletMutate();
      amountBreakdownMutate();
    }
  }, [isOpen, amountBreakdownMutate, walletMutate]);

  async function handlePayment() {
    setIsProcessingPayment(true);
    props.onInitiated?.();

    // const validateAmount = await validateCartTotalAmount(
    //   paymentPayload.amount
    //     ? paymentPayload.amount
    //     : (amountBreakdown?.amount ?? 0),
    // );
    // if (!validateAmount) {
    //   setIsProcessingPayment(false);
    //   return;
    // }
    const payload = {
      amount: amountBreakdown?.amount,
      paymentProvider: PaymentMethods.WALLET,
      paymentEntity: props.paymentEntityType,
      paymentEntityId: props.entityId,
      description: amountBreakdown?.description,
      metadata: props.metadata,
    };
    const response = await initiatePayment(payload);
    if (response.error) {
      onFailure(getErrorMessage(response));
      setIsProcessingPayment(false);
      return;
    }
    props.onPending?.();

    const verifyResponse = await verifyPayment({
      reference: response.id,
      provider: PaymentMethods.WALLET,
    });

    if (verifyResponse.error) {
      onFailure(getErrorMessage(verifyResponse));
      setIsProcessingPayment(false);
      return;
    }
    closeModal();
    await onSuccess();
    setIsProcessingPayment(false);
  }

  function hasWallet(): boolean {
    return !!wallet && !("error" in wallet) && Object.keys(wallet).length > 0;
  }

  function walletIsLow(): boolean {
    return (
      (wallet && !("error" in wallet) && Object.keys(wallet).length > 0
        ? wallet?.balance
        : 0) < (amountBreakdown?.amount ?? 0)
    );
  }

  return {
    isProcessingPayment,
    isLoading: isLoading || amountBreakdownIsLoading,
    isValidating: isValidating || amountBreakdownIsValidating,
    wallet,
    handlePayment,
    hasWallet: hasWallet(),
    lowBalance: walletIsLow(),
    openModal,
    closeModal,
    modalIsOpen,
    amountBreakdown,
  };
}
